import store from '@advisor/store';
import {getTokens, logout} from '@simpleinvest/front-end-auth';
import {
  NOTIFICATION_TYPE,
  RIA_GROUP_ID,
  ACCESS_LEVEL,
  SSO_REDIRECT_URI,
  ENTERPRISE_FEATURES,
} from '@advisor/util/constants';
import {getAzureToken} from '@advisor/config/useAuth';

// eslint-disable-next-line require-await
const maintenance = async (_to, _from, next) => {
  if (_to.name !== 'maintenance' && window.rwOptions.IS_UNDER_MAINTENANCE) {
    // eslint-disable-next-line callback-return
    next({name: 'maintenance'});
  }

  next();
};

// eslint-disable-next-line require-await
const startLoading = async (_to, _from, next) => {
  store.dispatch('setLoading', true);
  next();
};

// eslint-disable-next-line require-await
const checkPermissions = async (_to, _from, next) => {
  // check the meta.platformArea against theVuex platformAccess
  const {platformAccess} = store.state.settings.default;

  if (platformAccess[_to.meta.platformArea] === ACCESS_LEVEL.NO_ACCESS) {
    // eslint-disable-next-line no-console
    console.warn('beforeEach.checkPermissions', 'This user has no access to this page');
    next({name: 'App401'});

    return;
  }

  next();
};

const loadLayout = async (to, _from, next) => {
  if (!store.state.isLayoutSet) {
    await store.dispatch('getLayout', to.name);
  }

  next();
};

const getBaseUrl = () => {
  let baseUrl = window.BASE_URL;

  // if SSO use the SSO URL
  const enterpriseFeaturesArray = store?.state?.settings?.enterprise?.features;

  if (Array.isArray(enterpriseFeaturesArray) && enterpriseFeaturesArray.includes(ENTERPRISE_FEATURES.SSO)) {
    baseUrl = baseUrl.replace(/\/?$/, '/');
    baseUrl += SSO_REDIRECT_URI;
  }

  return baseUrl;
};

/**
 * This gets the business user (as opposed to the cognito user). One day they will be the same
 * @param {*} to
 * @param {*} _from
 * @param {*} next
 */
// eslint-disable-next-line max-statements
const getUser = async (to, _from, next) => {
  if (to.meta.unguarded) {
    next();

    return;
  }

  const token = getAzureToken();

  if (token === null) {
    try {
      await getTokens();
    } catch {
      // eslint-disable-next-line no-console
      console.warn('beforeEach.getUser', 'User not set, redirecting to login');

      window.location.href = getBaseUrl() + (to.path ? `?redirect=${to.path}` : '');

      return;
    }
  }

  if (!store.state.user.profile || !store.state.user.profile.riaUserId) {
    try {
      await store.dispatch('user/getUser');
    } catch {
      console.warn('beforeEach.getUser', 'Invalid user, redirecting to login');

      window.location.href = getBaseUrl() + (to.path ? `?redirect=${to.path}` : '');

      return;
    }

    // getting all dependencies
    try {
      await store.state.settings.getAllDeps(store);
    } catch {
      console.warn('beforeEach.getUser', 'Invalid dependencies, redirecting to login');

      window.location.href = getBaseUrl() + (to.path ? `?redirect=${to.path}` : '');

      return;
    }
  }

  if (store.state.user.profile.riaGroupId === RIA_GROUP_ID.NO_ACCESS) {
    await store.dispatch('notification/setMessage', {
      type: NOTIFICATION_TYPE.ERROR,
      text: 'Access denied. Please contact your administrator.',
      timeout: 5,
    });
    await logout();
    window.location.href = `${getBaseUrl()}?accessDenied=true`;

    return;
  }

  next();
};
/**
 * Set the value for window.BASE_URL
 */
const setBaseUrl = (to, from, next) => {
  let BASE_URL = window.location.origin;
  const BASE_FOLDER = process.env.VUE_APP_BASE_FOLDER;

  if (BASE_FOLDER) {
    BASE_URL = `${BASE_URL}/${BASE_FOLDER}`;
  }

  window.BASE_URL = BASE_URL;

  next();
};

// eslint-disable-next-line require-await
const setRiaComponent = async (to, from, next) => {
  const riaComponent = to.meta.riaComponent ? to.meta.riaComponent : null;

  store.dispatch('settings/setRiaComponent', {riaComponent});

  next();
};

/**
 * Method to load performance data dates per custodian, in store.
 * This data will be useful in getting last reliable date for given custodian,
 * to run performance data against
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
const setCustodianPerformanceDates = (to, from, next) => {
  if (to.meta.unguarded) {
    next();

    return;
  }

  if (!store.state.custodian.custodianPerformanceDateMap) {
    store.dispatch('custodian/custodianPerformanceDateMap');
  }

  next();
};

// eslint-disable-next-line import/prefer-default-export
export const beforeEach = [
  maintenance,
  setBaseUrl,
  setRiaComponent,
  startLoading,
  loadLayout,
  getUser,
  checkPermissions,
  setCustodianPerformanceDates,
];
